import { EngineConfig, Point2D } from '../types';

export class SVGRenderer {
  public svg: SVGSVGElement;
  public layers: {
    background: SVGGElement;
    grid: SVGGElement;
    constructions: SVGGElement;
    objects: SVGGElement;
    highlights: SVGGElement;
    labels: SVGGElement;
    tools: SVGGElement;
  };
  public config: Required<EngineConfig>;

  private static SVG_NS = 'http://www.w3.org/2000/svg';

  constructor(containerOrSvg: HTMLElement | SVGSVGElement, config: EngineConfig = {}) {
    this.config = {
      animationSpeed: config.animationSpeed ?? 1.0,
      lineColor: config.lineColor ?? '#1e293b',
      helperColor: config.helperColor ?? '#94a3b8',
      constructionColor: config.constructionColor ?? '#2563eb',
      highlightColor: config.highlightColor ?? '#f59e0b',
      pointRadius: config.pointRadius ?? 5,
      fontFamily: config.fontFamily ?? 'system-ui, -apple-system, sans-serif',
      fontSize: config.fontSize ?? 14,
      showCompass: config.showCompass ?? true,
      showRuler: config.showRuler ?? true,
      showLabels: config.showLabels ?? true,
      showConstructionMarks: config.showConstructionMarks ?? true,
      backgroundColor: config.backgroundColor ?? '#ffffff',
    };

    if (containerOrSvg instanceof SVGSVGElement) {
      this.svg = containerOrSvg;
    } else {
      this.svg = document.createElementNS(SVGRenderer.SVG_NS, 'svg') as SVGSVGElement;
      containerOrSvg.appendChild(this.svg);
    }

    this.setupSVG();
    this.layers = this.createLayers();
    this.setupDefs();
    this.renderGrid();
  }

  private setupSVG(): void {
    this.svg.setAttribute('width', '100%');
    this.svg.setAttribute('height', '100%');
    this.svg.setAttribute('viewBox', '0 0 1000 700');
    this.svg.style.backgroundColor = this.config.backgroundColor;
    this.svg.style.display = 'block';
    this.svg.style.userSelect = 'none';
    this.svg.style.fontFamily = this.config.fontFamily;
  }

  private createLayers() {
    const layerNames = ['background', 'grid', 'constructions', 'objects', 'highlights', 'labels', 'tools'] as const;
    const layers = {} as SVGRenderer['layers'];

    layerNames.forEach((name) => {
      const g = document.createElementNS(SVGRenderer.SVG_NS, 'g') as SVGGElement;
      g.setAttribute('id', `layer-${name}`);
      if (name === 'constructions' || name === 'objects' || name === 'highlights' || name === 'tools' || name === 'labels') {
        g.setAttribute('clip-path', 'url(#board-clip)');
      }
      this.svg.appendChild(g);
      layers[name] = g;
    });

    return layers;
  }

  private setupDefs(): void {
    const defs = document.createElementNS(SVGRenderer.SVG_NS, 'defs') as SVGDefsElement;

    // Glow filter for highlights
    const filter = document.createElementNS(SVGRenderer.SVG_NS, 'filter') as SVGFilterElement;
    filter.setAttribute('id', 'glow-filter');
    filter.setAttribute('x', '-50%');
    filter.setAttribute('y', '-50%');
    filter.setAttribute('width', '200%');
    filter.setAttribute('height', '200%');

    const feGaussianBlur = document.createElementNS(SVGRenderer.SVG_NS, 'feGaussianBlur') as SVGFEGaussianBlurElement;
    feGaussianBlur.setAttribute('stdDeviation', '4');
    feGaussianBlur.setAttribute('result', 'coloredBlur');

    const feMerge = document.createElementNS(SVGRenderer.SVG_NS, 'feMerge') as SVGFEMergeElement;
    const feMergeNode1 = document.createElementNS(SVGRenderer.SVG_NS, 'feMergeNode') as SVGFEMergeNodeElement;
    feMergeNode1.setAttribute('in', 'coloredBlur');
    const feMergeNode2 = document.createElementNS(SVGRenderer.SVG_NS, 'feMergeNode') as SVGFEMergeNodeElement;
    feMergeNode2.setAttribute('in', 'SourceGraphic');

    feMerge.appendChild(feMergeNode1);
    feMerge.appendChild(feMergeNode2);
    filter.appendChild(feGaussianBlur);
    filter.appendChild(feMerge);
    defs.appendChild(filter);

    // Board clipping path to prevent drawing overflow
    const clipPath = document.createElementNS(SVGRenderer.SVG_NS, 'clipPath') as SVGClipPathElement;
    clipPath.setAttribute('id', 'board-clip');
    const clipRect = document.createElementNS(SVGRenderer.SVG_NS, 'rect') as SVGRectElement;
    clipRect.setAttribute('x', '0');
    clipRect.setAttribute('y', '0');
    clipRect.setAttribute('width', '1000');
    clipRect.setAttribute('height', '700');
    clipPath.appendChild(clipRect);
    defs.appendChild(clipPath);

    // Arrowhead marker
    const marker = document.createElementNS(SVGRenderer.SVG_NS, 'marker') as SVGMarkerElement;
    marker.setAttribute('id', 'arrow-head');
    marker.setAttribute('viewBox', '0 0 10 10');
    marker.setAttribute('refX', '6');
    marker.setAttribute('refY', '5');
    marker.setAttribute('markerWidth', '6');
    marker.setAttribute('markerHeight', '6');
    marker.setAttribute('orient', 'auto-start-reverse');

    const path = document.createElementNS(SVGRenderer.SVG_NS, 'path') as SVGPathElement;
    path.setAttribute('d', 'M 0 1 L 10 5 L 0 9 z');
    path.setAttribute('fill', this.config.lineColor);
    marker.appendChild(path);
    defs.appendChild(marker);

    this.svg.appendChild(defs);
  }

  public renderGrid(): void {
    this.layers.grid.innerHTML = '';
    const pattern = document.createElementNS(SVGRenderer.SVG_NS, 'pattern') as SVGPatternElement;
    pattern.setAttribute('id', 'grid-pattern');
    pattern.setAttribute('width', '40');
    pattern.setAttribute('height', '40');
    pattern.setAttribute('patternUnits', 'userSpaceOnUse');

    const path = document.createElementNS(SVGRenderer.SVG_NS, 'path') as SVGPathElement;
    path.setAttribute('d', 'M 40 0 L 0 0 0 40');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'rgba(148, 163, 184, 0.15)');
    path.setAttribute('stroke-width', '1');

    pattern.appendChild(path);
    
    let defs = this.svg.querySelector('defs') as SVGDefsElement | null;
    if (!defs) {
      defs = document.createElementNS(SVGRenderer.SVG_NS, 'defs') as SVGDefsElement;
      this.svg.appendChild(defs);
    }
    defs.appendChild(pattern);

    const rect = document.createElementNS(SVGRenderer.SVG_NS, 'rect') as SVGRectElement;
    rect.setAttribute('width', '1000');
    rect.setAttribute('height', '700');
    rect.setAttribute('fill', 'url(#grid-pattern)');
    this.layers.grid.appendChild(rect);
  }

  public createElement<K extends keyof SVGElementTagNameMap>(
    tagName: K,
    attributes: Record<string, string | number> = {}
  ): SVGElementTagNameMap[K] {
    const elem = document.createElementNS(SVGRenderer.SVG_NS, tagName) as SVGElementTagNameMap[K];
    for (const [key, val] of Object.entries(attributes)) {
      elem.setAttribute(key, String(val));
    }
    return elem;
  }

  public setViewBox(x: number, y: number, width: number, height: number): void {
    this.svg.setAttribute('viewBox', `${x} ${y} ${width} ${height}`);
  }

  // --- Math Utility Helpers ---

  public static distance(p1: Point2D, p2: Point2D): number {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  public static angle(center: Point2D, p: Point2D): number {
    return Math.atan2(p.y - center.y, p.x - center.x);
  }

  public static polarToCartesian(center: Point2D, radius: number, angleRad: number): Point2D {
    return {
      x: center.x + radius * Math.cos(angleRad),
      y: center.y + radius * Math.sin(angleRad),
    };
  }

  /**
   * Describes an SVG arc path from startAngleRad to endAngleRad around center.
   * @param sweepCW - true = clockwise (SVG sweep-flag=1), false = counterclockwise (sweep-flag=0).
   *                  The function always draws the arc in the stated sweep direction.
   *                  If the resulting span would be > 180° (large arc), it automatically
   *                  flips sweep direction to use the shorter arc instead.
   */
  public static describeArcPath(
    center: Point2D,
    radius: number,
    startAngleRad: number,
    endAngleRad: number,
    sweepCW: boolean = true
  ): string {
    const start = SVGRenderer.polarToCartesian(center, radius, startAngleRad);
    const end = SVGRenderer.polarToCartesian(center, radius, endAngleRad);

    // Compute the angular span in the chosen sweep direction
    let span: number;
    if (sweepCW) {
      span = endAngleRad - startAngleRad;
      while (span < 0) span += Math.PI * 2;
      while (span >= Math.PI * 2) span -= Math.PI * 2;
    } else {
      span = startAngleRad - endAngleRad;
      while (span < 0) span += Math.PI * 2;
      while (span >= Math.PI * 2) span -= Math.PI * 2;
    }

    // Safety: if span >= PI, the caller has start/end in the wrong order for this sweep direction.
    // Flip to the complementary direction to always draw the short arc.
    let effectiveSweepCW = sweepCW;
    if (span > Math.PI) {
      effectiveSweepCW = !sweepCW;
      span = Math.PI * 2 - span;
    }

    const largeArcFlag = span > Math.PI ? '1' : '0';
    const sweepFlag = effectiveSweepCW ? '1' : '0';

    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} ${sweepFlag} ${end.x} ${end.y}`;
  }

  public clearLayer(layerName: keyof SVGRenderer['layers']): void {
    this.layers[layerName].innerHTML = '';
  }

  public clearAll(): void {
    Object.values(this.layers).forEach((layer) => {
      layer.innerHTML = '';
    });
    this.renderGrid();
  }
}
